import { useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { type Brand } from '@redteclab/api/clients/bully'

import { type BrandOption } from './useMarketplacePartnerRegisterPageAutocompleteState'
import { useMarketplacePartnerRegisterPageBrands } from './useMarketplacePartnerRegisterPageBrands'
import { useMarketplacePartnerRegisterPageSearchBrands } from './useMarketplacePartnerRegisterPageSearchBrands'

type UseMarketplacePartnerRegisterPageBrandsSearchInfiniteParams = {
  query: string
}

type UseMarketplacePartnerRegisterPageBrandsSearchInfiniteReturn = {
  hasMore: boolean
  isLoading: boolean
  loadMore: () => void
  options: BrandOption[]
  query: string
}

const DEBOUNCE_DELAY_MS = 300

export const useMarketplacePartnerRegisterPageBrandsSearchInfinite = ({
  query,
}: UseMarketplacePartnerRegisterPageBrandsSearchInfiniteParams): UseMarketplacePartnerRegisterPageBrandsSearchInfiniteReturn => {
  const {
    allBrands: baseBrands,
    hasMore: baseHasMore,
    isLoading: isBaseLoading,
    loadMore: baseLoadMore,
  } = useMarketplacePartnerRegisterPageBrands()

  const [debouncedQuery] = useDebounceValue(query, DEBOUNCE_DELAY_MS)
  const { isLoading: isSearchLoading, searchBrands } =
    useMarketplacePartnerRegisterPageSearchBrands({ query: debouncedQuery })

  const [displayBrands, setDisplayBrands] = useState<Brand[]>([])

  useEffect(() => {
    if (!query) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
      setDisplayBrands(baseBrands)

      return
    }

    if (
      debouncedQuery !== query ||
      isSearchLoading ||
      searchBrands === undefined
    ) {
      return
    }

    setDisplayBrands(searchBrands)
  }, [query, baseBrands, isSearchLoading, searchBrands, debouncedQuery])

  const options = useMemo<BrandOption[]>(() => {
    return displayBrands.map((brand) => ({
      label: brand.brandName,
      value: brand.code,
    }))
  }, [displayBrands])

  const hasMore = !debouncedQuery && baseHasMore
  const isLoading = debouncedQuery ? isSearchLoading : isBaseLoading
  const loadMore = (): void => {
    if (!debouncedQuery) {
      baseLoadMore()
    }
  }

  return {
    hasMore,
    isLoading,
    loadMore,
    options,
    query,
  }
}
